import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Minus, Plus, ShoppingBag, ShoppingCart, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api, { apiError, API_BASE } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function loadRazorpay() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve;
    s.onerror = () => reject(new Error("Could not load payment gateway"));
    document.body.appendChild(s);
  });
}

const input = "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-navy outline-none focus:border-saffron";

export default function Shop() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [catalog, setCatalog] = useState({ categories: [], products: [] });
  const [contact, setContact] = useState(null);
  const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem("ifx_cart") || "[]"));
  const [cartOpen, setCartOpen] = useState(false);
  const [checkout, setCheckout] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [payBusy, setPayBusy] = useState(false);
  const [paid, setPaid] = useState(false);
  const [detail, setDetail] = useState(null);
  const [addr, setAddr] = useState({ name: "", phone: "", address: "", city: "", pincode: "", notes: "" });

  const activeCat = params.get("cat") || "";
  const activeSub = params.get("sub") || "";

  useEffect(() => {
    api.get("/shop/public/catalog").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/public/home").then((r) => setContact(r.data.contact)).catch(() => {});
  }, []);

  const persist = (c) => { setCart(c); localStorage.setItem("ifx_cart", JSON.stringify(c)); };
  const add = (p) => {
    const ex = cart.find((i) => i.id === p.id);
    persist(ex ? cart.map((i) => i.id === p.id ? { ...i, qty: Math.min(i.qty + 1, 50) } : i)
              : [...cart, { id: p.id, name: p.name, price: p.price, qty: 1 }]);
    toast.success(`${p.name} added to cart`);
  };
  const setQty = (id, d) => persist(cart.map((i) => i.id === id ? { ...i, qty: Math.max(1, Math.min(50, i.qty + d)) } : i));
  const removeItem = (id) => persist(cart.filter((i) => i.id !== id));
  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  const cat = catalog.categories.find((c) => c.slug === activeCat);
  const subs = cat?.subs || [];
  const products = useMemo(() => catalog.products.filter((p) => {
    if (activeCat && p.category_name !== cat?.name) return false;
    if (activeSub && p.subcategory !== activeSub) return false;
    return true;
  }), [catalog, activeCat, activeSub, cat]);

  const pickCat = (slug) => setParams(slug ? { cat: slug } : {});
  const pickSub = (s) => setParams({ cat: activeCat, ...(s ? { sub: s } : {}) });

  const openCheckout = () => {
    if (!user) {
      toast.info("Please login as a customer to complete your order");
      navigate("/login", { state: { next: "/shop?checkout=1" } });
      return;
    }
    setAddr({ name: user.name || "", phone: user.phone || "", address: "", city: "", pincode: "", notes: "" });
    setCheckout(true);
  };

  useEffect(() => {
    if (user && params.get("checkout") === "1" && cart.length) {
      setAddr((a) => ({ ...a, name: user.name || "", phone: user.phone || "" }));
      setCheckout(true);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const placeOrder = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/shop/orders", { items: cart.map((i) => ({ product_id: i.id, qty: i.qty })), ...addr });
      setDone(res.data);
      persist([]);
      setCheckout(false);
      setCartOpen(false);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const payOnline = async () => {
    setPayBusy(true);
    try {
      const res = await api.post(`/shop/orders/${done.id}/razorpay-order`, {});
      await loadRazorpay();
      const rzp = new window.Razorpay({
        key: res.data.key_id, amount: res.data.amount, currency: "INR", order_id: res.data.order_id,
        name: "INDIAFAST Global Couriers", description: `Shop Order ${done.order_id}`, theme: { color: "#FF5A1F" },
        handler: async (resp) => {
          try {
            await api.post(`/shop/orders/${done.id}/razorpay-verify`, {
              order_id: resp.razorpay_order_id, payment_id: resp.razorpay_payment_id, signature: resp.razorpay_signature });
            setPaid(true);
            toast.success("Payment successful!");
          } catch (err) { toast.error(apiError(err)); }
        },
        modal: { ondismiss: () => toast.info("Payment cancelled — you can also pay on delivery") },
      });
      rzp.open();
    } catch (err) { toast.error(apiError(err)); }
    finally { setPayBusy(false); }
  };

  const imgUrl = (p) => `${API_BASE}/api/shop/public/image/${p.id}`;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="shop-page">
      <Navbar />
      <section className="bg-navy pb-12 pt-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-saffron">INDIAFAST BAZAAR</p>
          <h1 className="mt-2 font-heading text-3xl font-extrabold text-white sm:text-4xl">India's Bazaar, IndiaFast Delivery</h1>
          <p className="mt-2 text-sm text-slate-300">Order authentic Indian food items and gifts for your loved ones abroad — we pack, ship and deliver to 220+ countries.</p>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        {done && (
          <div className="mb-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="shop-order-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
            <h2 className="mt-3 font-heading text-xl font-bold text-navy">Order Placed!</h2>
            <p className="mt-1 text-sm text-slate-600">Order <span className="font-mono font-bold text-express" data-testid="shop-order-id">{done.order_id}</span> · Total ₹{done.total}</p>
            {paid ? (
              <p className="mt-2 inline-block rounded-full bg-emerald-100 px-4 py-1.5 text-xs font-bold text-emerald-700" data-testid="shop-paid-badge">✓ Paid online via Razorpay</p>
            ) : (
              <div className="mx-auto mt-4 max-w-xs">
                <button onClick={payOnline} disabled={payBusy} data-testid="shop-pay-online-btn"
                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-navy py-3 text-sm font-bold text-white hover:bg-express disabled:opacity-60">
                  {payBusy && <Loader2 className="h-4 w-4 animate-spin" />} Pay Online ₹{done.total} — UPI / Cards
                </button>
                <p className="mt-2 text-[11px] text-slate-400">or pay on delivery — our team confirms every order on call.</p>
              </div>
            )}
            <p className="mt-3 text-xs text-slate-500">Track it anytime in your dashboard → My Orders.</p>
          </div>
        )}

        <div className="mb-4 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="shop-categories">
          <button onClick={() => pickCat("")} data-testid="shop-cat-all"
                  className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold ${!activeCat ? "bg-navy text-white" : "border border-slate-200 bg-white text-slate-600"}`}>
            All Products
          </button>
          {catalog.categories.map((c) => (
            <button key={c.id} onClick={() => pickCat(c.slug)} data-testid={`shop-cat-${c.slug}`}
                    className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold ${activeCat === c.slug ? "bg-navy text-white" : "border border-slate-200 bg-white text-slate-600"}`}>
              {c.name}
            </button>
          ))}
        </div>
        {subs.length > 0 && (
          <div className="mb-6 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="shop-subcategories">
            <button onClick={() => pickSub("")} data-testid="shop-sub-all"
                    className={`shrink-0 rounded-full px-3 py-1.5 text-[11px] font-semibold ${!activeSub ? "bg-saffron text-white" : "bg-slate-200 text-slate-600"}`}>
              All {cat?.name}
            </button>
            {subs.map((s) => (
              <button key={s} onClick={() => pickSub(s)} data-testid={`shop-sub-${s.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`shrink-0 rounded-full px-3 py-1.5 text-[11px] font-semibold ${activeSub === s ? "bg-saffron text-white" : "bg-slate-200 text-slate-600"}`}>
                {s}
              </button>
            ))}
          </div>
        )}

        {products.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-14 text-center" data-testid="shop-empty">
            <ShoppingBag className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 text-sm font-semibold text-navy">No products here yet</p>
            <p className="mt-1 text-xs text-slate-500">New stock is being added — check back soon.</p>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="shop-products">
            {products.map((p) => (
              <div key={p.id} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg" data-testid={`shop-product-${p.id}`}>
                <button type="button" onClick={() => setDetail(p)} data-testid={`shop-detail-open-${p.id}`} title="View details"
                        className="relative block h-48 w-full overflow-hidden bg-white p-2">
                  {p.image_path ? (
                    <img src={imgUrl(p)} alt={p.name} className="h-full w-full object-contain transition-transform group-hover:scale-105" />
                  ) : (
                    <div className="grid h-full place-items-center bg-slate-50"><ShoppingBag className="h-10 w-10 text-slate-300" /></div>
                  )}
                  {p.stock <= 0 && <span className="absolute inset-0 grid place-items-center bg-white/70 text-xs font-bold text-red-500">Out of stock</span>}
                  <span className="absolute bottom-2 right-2 rounded-full bg-navy/80 px-2.5 py-1 text-[9px] font-bold text-white opacity-0 transition-opacity group-hover:opacity-100">View Details</span>
                </button>
                <div className="p-4">
                  {p.subcategory && <p className="text-[10px] font-bold uppercase tracking-wider text-saffron">{p.subcategory}</p>}
                  <h3 className="mt-0.5 font-heading text-sm font-bold text-navy">{p.name}</h3>
                  {p.description && <p className="mt-1 line-clamp-2 text-xs text-slate-500">{p.description}</p>}
                  <div className="mt-3 flex items-center justify-between">
                    <p className="font-heading text-lg font-extrabold text-navy">₹{p.price}</p>
                    <button onClick={() => add(p)} disabled={p.stock <= 0} data-testid={`shop-add-${p.id}`}
                            className="flex items-center gap-1.5 rounded-full bg-saffron px-3.5 py-2 text-xs font-bold text-white shadow-md shadow-saffron/30 hover:bg-saffron-hover disabled:opacity-40">
                      <ShoppingCart className="h-3.5 w-3.5" /> Add
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {cartCount > 0 && !cartOpen && !checkout && (
        <button onClick={() => setCartOpen(true)} data-testid="shop-cart-fab"
                className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-navy px-5 py-3.5 text-sm font-bold text-white shadow-2xl hover:bg-express">
          <ShoppingCart className="h-5 w-5" /> Cart · {cartCount} · ₹{total.toFixed(0)}
        </button>
      )}

      {cartOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/50" data-testid="shop-cart-drawer">
          <div className="flex h-full w-full max-w-md flex-col bg-white">
            <div className="flex items-center justify-between border-b border-slate-200 p-5">
              <h3 className="font-heading text-lg font-bold text-navy">Your Cart ({cartCount})</h3>
              <button onClick={() => setCartOpen(false)} data-testid="shop-cart-close"><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto p-5">
              {cart.map((i) => (
                <div key={i.id} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3" data-testid={`cart-item-${i.id}`}>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-navy">{i.name}</p>
                    <p className="text-xs text-slate-500">₹{i.price} each</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setQty(i.id, -1)} className="rounded-lg bg-slate-100 p-1.5" data-testid={`cart-minus-${i.id}`}><Minus className="h-3.5 w-3.5" /></button>
                    <span className="w-6 text-center text-sm font-bold">{i.qty}</span>
                    <button onClick={() => setQty(i.id, 1)} className="rounded-lg bg-slate-100 p-1.5" data-testid={`cart-plus-${i.id}`}><Plus className="h-3.5 w-3.5" /></button>
                  </div>
                  <p className="w-16 text-right text-sm font-bold text-navy">₹{(i.price * i.qty).toFixed(0)}</p>
                  <button onClick={() => removeItem(i.id)} data-testid={`cart-remove-${i.id}`}><Trash2 className="h-4 w-4 text-red-400" /></button>
                </div>
              ))}
              {!cart.length && <p className="py-10 text-center text-sm text-slate-400">Cart is empty</p>}
            </div>
            <div className="border-t border-slate-200 p-5">
              <div className="mb-4 flex justify-between text-sm font-bold text-navy">
                <span>Total</span><span data-testid="cart-total">₹{total.toFixed(2)}</span>
              </div>
              <button onClick={openCheckout} disabled={!cart.length} data-testid="shop-checkout-btn"
                      className="w-full rounded-xl bg-saffron py-3.5 text-sm font-bold text-white shadow-lg shadow-saffron/30 hover:bg-saffron-hover disabled:opacity-50">
                {user ? "Proceed to Checkout" : "Login to Checkout"}
              </button>
            </div>
          </div>
        </div>
      )}

      {checkout && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="shop-checkout-modal">
          <form onSubmit={placeOrder} className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold text-navy">Delivery Details</h3>
              <button type="button" onClick={() => setCheckout(false)} data-testid="checkout-close"><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="mt-4 space-y-3">
              <input required className={input} data-testid="checkout-name" placeholder="Full name" value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })} />
              <input required className={input} data-testid="checkout-phone" placeholder="Phone" value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} />
              <input required className={input} data-testid="checkout-address" placeholder="Full address" value={addr.address} onChange={(e) => setAddr({ ...addr, address: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <input required className={input} data-testid="checkout-city" placeholder="City" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} />
                <input required className={input} data-testid="checkout-pincode" placeholder="Pincode" value={addr.pincode} onChange={(e) => setAddr({ ...addr, pincode: e.target.value })} />
              </div>
              <textarea rows={2} className={input} data-testid="checkout-notes" placeholder="Notes (optional)" value={addr.notes} onChange={(e) => setAddr({ ...addr, notes: e.target.value })} />
            </div>
            <div className="mt-4 flex justify-between rounded-lg bg-slate-50 px-4 py-3 text-sm font-bold text-navy">
              <span>{cartCount} item(s)</span><span>₹{total.toFixed(2)}</span>
            </div>
            <button disabled={busy} data-testid="checkout-place-order"
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-saffron py-3.5 text-sm font-bold text-white hover:bg-saffron-hover disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Place Order
            </button>
          </form>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" data-testid="shop-detail-modal" onClick={() => setDetail(null)}>
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="grid sm:grid-cols-2">
              <div className="grid h-64 place-items-center bg-slate-50 p-4 sm:h-auto sm:min-h-[320px]">
                {detail.image_path ? (
                  <img src={imgUrl(detail)} alt={detail.name} className="max-h-72 w-full object-contain" data-testid="detail-img" />
                ) : (
                  <ShoppingBag className="h-14 w-14 text-slate-300" />
                )}
              </div>
              <div className="p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    {(detail.category_name || detail.subcategory) && (
                      <p className="text-[10px] font-bold uppercase tracking-wider text-saffron" data-testid="detail-category">
                        {[detail.category_name, detail.subcategory].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    <h3 className="mt-1 font-heading text-xl font-bold text-navy" data-testid="detail-name">{detail.name}</h3>
                  </div>
                  <button onClick={() => setDetail(null)} data-testid="detail-close" aria-label="Close"><X className="h-5 w-5 text-slate-400" /></button>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate-600" data-testid="detail-description">{detail.description || "No description added yet."}</p>
                <p className={`mt-2 text-xs font-bold ${detail.stock > 0 ? "text-emerald-600" : "text-red-500"}`} data-testid="detail-stock">
                  {detail.stock > 0 ? `${detail.stock} in stock` : "Out of stock"}
                </p>
                <p className="mt-4 font-heading text-2xl font-extrabold text-navy" data-testid="detail-price">₹{detail.price}</p>
                <button onClick={() => { add(detail); setDetail(null); setCartOpen(true); }} disabled={detail.stock <= 0} data-testid="detail-add-btn"
                        className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-saffron py-3 text-sm font-bold text-white shadow-lg shadow-saffron/30 hover:bg-saffron-hover disabled:opacity-40">
                  <ShoppingCart className="h-4 w-4" /> Add to Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer contact={contact} />
    </div>
  );
}
